import { defaultTimeZone } from '#api/common.ts';
import { now, nowMs } from '#lib/services/time.ts';
import { Temporal } from 'temporal-polyfill';
import type { Selected } from 'bits-ui';

export const MIN_DATE = new Date(0);

/**
 * @returns {Date}  The UTC date for tomorrow at midnight.
 */
export function getTomorrowUTC(): Date {
    const date = now();
    date.setUTCDate(date.getUTCDate() + 1);
    date.setUTCHours(0, 0, 0, 0);
    return date;
}

/**
 * @returns {Date}  The local date for tomorrow at midnight.
 */
export function getTomorrow(): Date {
    const date = now();
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
}

/**
 * @returns {Date}  The UTC date for today at midnight.
 */
export function getTodayUTC(): Date {
    const date = now();
    date.setUTCHours(0, 0, 0, 0);
    return date;
}

/**
 * @returns {Date}  The local date for today at midnight.
 */
export function getToday(): Date {
    const date = now();
    date.setHours(0, 0, 0, 0);
    return date;
}

/** Now in a time zone (the browser's by default), following time travel — use instead of Temporal.Now. */
export function nowZoned(timeZone: string = Temporal.Now.timeZoneId()): Temporal.ZonedDateTime {
    return Temporal.Instant.fromEpochMilliseconds(nowMs()).toZonedDateTimeISO(timeZone);
}

export function getTodayPlainDate(timeZone: string = defaultTimeZone): Temporal.PlainDate {
    return nowZoned(timeZone).toPlainDate();
}

export function getClientToday(timeZone: string = defaultTimeZone): string {
    return getTodayPlainDate(timeZone).toString();
}

export function getClientTomorrow(timeZone: string = defaultTimeZone): string {
    return getTodayPlainDate(timeZone).add({ days: 1 }).toString();
}

/**
 * @returns {Date} The first day of the next month at midnight.
 */
export function getNextMonth(): Date {
    const date = now();
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCMonth(date.getUTCMonth() + 1);
    return date;
}

/**
 * Gets the provided local date treated as a UTC date at midnight.
 *
 * This function takes a local date (ignoring the time) and retrieves the
 * corresponding UTC date, where the time is set to midnight (00:00:00).
 * The focus is on the **calendar date** without considering any time zone offsets.
 *
 * **Important Note:**
 * The function does not apply any time zone offsets or time adjustments.
 * It simply treats the provided local date as a UTC date at midnight.
 *
 * One application of this method is to capture the **customer's intended payment date**
 * (rather than the actual processing date in UTC), aligning with the user's
 * perspective based on their local time zone.
 *
 * @param {Date} date The local date for which to get the corresponding UTC date.
 * @returns {Date} A `Date` object representing the corresponding UTC date at midnight
 * for the provided local date.
 *
 * | TZ  | Offset | Input (Local)           | UTC                     | Return Value            |
 * |:---:|-------:|------------------------:|------------------------:|------------------------:|
 * | CST | -06:00 | 2025-01-02 20:00:00 CST | 2025-01-03 02:00:00 UTC | 2025-01-02 00:00:00 UTC |
 * | PST | -08:00 | 2025-01-02 20:00:00 PST | 2025-01-03 04:00:00 UTC | 2025-01-02 00:00:00 UTC |
 * | EET | +02:00 | 2025-01-02 01:00:00 EET | 2025-01-01 23:00:00 UTC | 2025-01-02 00:00:00 UTC |
 */
export function getLocalDateAsUTCDate(date: Date) {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
}

export function getTodayAsUTCDate(): Date {
    return getLocalDateAsUTCDate(getToday());
}

/**
 * @returns {string} the local ISO date string (YYYY-MM-DD) for the given Date.
 */
export function isoDateString(date: Date): string {
    return date.toISOString().split('T')[0];
}

/**
 * The local calendar day of a Date as 'YYYY-MM-DD', for APIs that want a plain day
 * rather than an instant. `isoDateString` alone reads UTC parts, so an evening local
 * time would report tomorrow.
 */
export function calendarDayString(date: Date): string {
    return isoDateString(getLocalDateAsUTCDate(date));
}

/**
 * @param {string} isoDateString - An ISO date string (YYYY-MM-DD).
 * @returns {Date} When parse is successful, a date instance for the given string. Otherwise, the minimum date.
 */
export function parseDate(isoDateString: string): Date {
    const t = Date.parse(isoDateString);
    return isNaN(t) ? MIN_DATE : new Date(t);
}

/**
 * Generates an array of month options
 * @param {string} [locale='en-US'] - The locale to use for formatting the month labels.
 */
export function generateMonthOptions(locale: string = 'en-US'): Selected<string>[] {
    return Array(12)
        .fill(0)
        .map((_, i) => {
            const month = new Date(0, i);

            return {
                label: `${month.toLocaleString(locale, { month: '2-digit' })} - ${month.toLocaleString(locale, { month: 'long' })}`,
                value: (month.getMonth() + 1).toString()
            };
        });
}

/**
 * Generates an array of future year options.
 * @param {number} [limit=20] - The number of years to include in the options.
 */
export function generateFutureYearOptions(limit: number = 20): Selected<string>[] {
    return Array(limit)
        .fill(getNextMonth().getFullYear())
        .map((year, i) => (year + i).toString())
        .map((value) => ({ label: value, value }));
}

export function convertToZonedDateTime(value: string, timeZone: string): Temporal.ZonedDateTime | undefined {
    if (!value) {
        return undefined;
    }

    const instant = Temporal.Instant.from(value);
    return instant.toZonedDateTimeISO(timeZone);
}

/**
 * Converts a ZonedDateTime to a string in yyyy-MM-ddThh:mm:ss format in a specific time zone
 *
 * @param {Temporal.Instant} zonedDateTime
 * @returns {string} - Formatted date-time string in yyyy-MM-ddThh:mm:ss format
 */
export function formatTemporalForInput(zonedDateTime: Temporal.ZonedDateTime): string {
    if (!zonedDateTime) {
        return '';
    }
    try {
        const year = String(zonedDateTime.year).padStart(4, '0');
        const month = String(zonedDateTime.month).padStart(2, '0');
        const day = String(zonedDateTime.day).padStart(2, '0');
        const hour = String(zonedDateTime.hour).padStart(2, '0');
        const minute = String(zonedDateTime.minute).padStart(2, '0');
        const second = String(zonedDateTime.second).padStart(2, '0');

        return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    } catch (error: unknown) {
        throw new Error(`Failed to format date: ${error instanceof Error ? error.message : String(error)}`, {
            cause: error
        });
    }
}

/**
 * Returns the UTC ISO string for the start of a given year in a specific timezone
 * @param {number} year - The year (e.g., 2025)
 * @param {string} timeZone - The IANA timezone (e.g., "America/New_York")
 * @returns {string} - UTC ISO string for start of year
 */
export function getYearStartDateTimeString(year: number, timeZone: string): string {
    const startTemporal = Temporal.ZonedDateTime.from({
        timeZone: timeZone,
        year: year,
        month: 1,
        day: 1,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
    });

    return startTemporal.toInstant().toString();
}

/**
 * Returns the UTC ISO string for the end of a given year in a specific timezone
 * @param {number} year - The year (e.g., 2025)
 * @param {string} timeZone - The IANA timezone (e.g., "America/New_York")
 * @returns {string} - UTC ISO string for end of year
 */
export function getYearEndDateTimeString(year: number, timeZone: string): string {
    const endTemporal = Temporal.ZonedDateTime.from({
        timeZone: timeZone,
        year: year,
        month: 12,
        day: 31,
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999
    });

    return endTemporal.toInstant().toString();
}

/**
 * Converts a DateOnly string to an ISO8601 UTC string at midnight in the specified timezone
 * @param {string} dateOnly - Date string in format 'YYYY-MM-DD'
 * @param {string} timeOnly - Time string in format 'HH:mm:ss   ' (default is '00:00:00')
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York', 'Europe/London', 'UTC')
 * @returns {string} ISO8601 formatted string in UTC
 */
export function dateOnlyToIsoUtc(dateOnly: string, timeOnly: string = '00:00:00', timezone = 'UTC') {
    const plainDate = Temporal.PlainDate.from(dateOnly);
    const plainTime = Temporal.PlainTime.from(timeOnly);

    const zonedDateTime = plainDate.toZonedDateTime({
        timeZone: timezone,
        plainTime: plainTime
    });

    // Convert to UTC Instant and return as ISO string
    return zonedDateTime.toInstant().toString();
}

/**
 * Parses a date-only string (YYYY-MM-DD) into a UTC Date without timezone shift.
 * Returns undefined for invalid or year <= 1 inputs.
 */
export function parseDateOnly(dateString: string): Date | undefined {
    if (!dateString) return undefined;

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString);
    if (!match) return undefined;

    const year = Number(match[1]);
    if (year <= 1) return undefined;

    const date = new Date(Date.UTC(year, Number(match[2]) - 1, Number(match[3])));
    if (isNaN(date.getTime())) return undefined;

    return date;
}

/** Today in the browser's zone, as 'YYYY-MM-DD'. Unlike getClientToday it uses the browser's zone, not the client's. */
export function getBrowserToday(): string {
    return nowZoned().toPlainDate().toString();
}

/** Adds months then days to a 'YYYY-MM-DD' date, clamping month-end like the server's DateOnly.AddMonths. */
export function shiftDateOnly(value: string, { months = 0, days = 0 }: { months?: number; days?: number }): string {
    return Temporal.PlainDate.from(value).add({ months }).add({ days }).toString();
}
